import React, { useEffect, useState } from "react";
import { Download, Monitor, Smartphone, Apple, CheckCircle2, Share, PlusSquare, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function InstallAppButton() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [activeTab, setActiveTab] = useState("desktop");

  useEffect(() => {
    // Check if running in standalone mode (already installed)
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    setInstalled(isStandalone);

    // Auto-detect platform for default tab
    const ua = window.navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) {
      setActiveTab("ios");
    } else if (/android/.test(ua)) {
      setActiveTab("android");
    } else {
      setActiveTab("desktop");
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };

    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
      setOpenModal(false);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") {
          setInstalled(true);
        }
        setDeferred(null);
        return;
      } catch (e) {
        // Fallback to modal if prompt fails
        setOpenModal(true);
      }
    } else {
      setOpenModal(true);
    }
  };

  if (installed) {
    return (
      <div
        data-testid="app-installed-badge"
        className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-[11px] font-medium text-muted-foreground"
      >
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
        LiveLife Intranet Installed
      </div>
    );
  }

  return (
    <>
      <button
        data-testid="install-app-button"
        onClick={handleInstallClick}
        className="w-full flex items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/20 text-primary px-3 py-2.5 text-xs font-semibold transition-all shadow-sm cursor-pointer"
      >
        <Download className="h-4 w-4 shrink-0 text-primary" />
        <span>Install LiveLife Intranet</span>
      </button>

      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent aria-describedby={undefined} className="max-w-md p-6">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="h-10 w-10 rounded-xl bg-white border border-border flex items-center justify-center overflow-hidden p-1 shadow-sm">
                <img src="/livelife-logo.webp" alt="LiveLife Education" className="h-8 w-auto object-contain" />
              </div>
              <div>
                <DialogTitle className="font-display text-lg font-bold">Install LiveLife Education Intranet</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">Save as a standalone app on your device</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Platform Selector Tabs */}
          <div className="flex rounded-lg border border-border p-1 bg-muted/40 gap-1 my-3">
            <button
              onClick={() => setActiveTab("desktop")}
              data-testid="install-tab-desktop"
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-semibold transition-colors ${
                activeTab === "desktop" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Monitor className="h-3.5 w-3.5" /> Desktop
            </button>
            <button
              onClick={() => setActiveTab("android")}
              data-testid="install-tab-android"
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-semibold transition-colors ${
                activeTab === "android" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Smartphone className="h-3.5 w-3.5" /> Android
            </button>
            <button
              onClick={() => setActiveTab("ios")}
              data-testid="install-tab-ios"
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-xs font-semibold transition-colors ${
                activeTab === "ios" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Apple className="h-3.5 w-3.5" /> iPhone / iPad
            </button>
          </div>

          {/* Tab Content */}
          <div className="space-y-4 py-1 text-sm">
            {activeTab === "desktop" && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Install on Windows or Mac via Chrome or Edge:</p>
                <div className="rounded-lg border border-border bg-card p-3 space-y-2">
                  <div className="flex items-start gap-2.5">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
                    <p className="text-xs leading-relaxed">
                      Look at the <strong>right side of your browser address bar (URL bar)</strong> at top right for the <strong>Install icon</strong> (<Download className="h-3 w-3 inline text-primary" /> or 💻 ⬇️).
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
                    <p className="text-xs leading-relaxed">
                      Click <strong>Install</strong> to add <strong>LiveLife Education Intranet</strong> to your Desktop or Taskbar.
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5 pt-1 border-t border-border/60">
                    <span className="text-[11px] text-muted-foreground">
                      <strong>Alternative:</strong> Click browser menu (<strong>⋮</strong> or <strong>…</strong>) → <em>"Cast, save, and share"</em> → <em>"Install page as app..."</em>.
                    </span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "android" && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Install on Android via Chrome / Edge / Samsung Internet:</p>
                <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
                  <div className="flex items-start gap-2.5">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
                    <p className="text-xs leading-relaxed">
                      Tap the <strong>three dots menu (⋮)</strong> at the top right of your browser.
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
                    <p className="text-xs leading-relaxed">
                      Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">3</span>
                    <p className="text-xs leading-relaxed">
                      Confirm <strong>"Install"</strong>. The app icon will appear on your home screen.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "ios" && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">Install on iPhone or iPad via Safari:</p>
                <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
                  <div className="flex items-start gap-2.5">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
                    <p className="text-xs leading-relaxed">
                      In Safari, tap the <strong>Share button</strong> <Share className="h-3.5 w-3.5 inline text-primary mx-0.5" /> (square with upward arrow) in the toolbar.
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
                    <p className="text-xs leading-relaxed">
                      Scroll down and tap <PlusSquare className="h-3.5 w-3.5 inline text-primary mx-0.5" /> <strong>"Add to Home Screen"</strong>.
                    </p>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">3</span>
                    <p className="text-xs leading-relaxed">
                      Tap <strong>"Add"</strong> at the top right. LiveLife Intranet will open in full-screen mode.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button size="sm" onClick={() => setOpenModal(false)}>Got it</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
